import type { ReactNode } from "react";
import { Button, HStack, Text } from "@chakra-ui/react";
import { LuX } from "react-icons/lu";

/**
 * The line directly above a filtered table: how many rows, out of how many.
 *
 * One sentence for every table. There were six -- "Filtered to 5 of 36
 * activities", a "Visible rows" stat card, "Showing 5 active Alumni out of
 * 287 people in total" -- each saying the same thing differently.
 *
 * "36 activities" when nothing narrows the view; "5 of 36 activities" and a
 * Clear filters button when something does. Clearing shows everything, even where the
 * page opens narrowed (CeMMies opens on the current CeMMies), so the line
 * also tells somebody that there is more than they are seeing.
 *
 * The noun is the rows', pluralised by the total. Which filter is on is
 * already said by the controls above it.
 */
export function FilterCount({
  shown,
  total,
  noun,
  filtered,
  onClear,
  children,
}: {
  shown: number;
  /** Null where the total is not known -- a list filtered on the server. */
  total: number | null;
  /** Singular and plural. */
  noun: [string, string];
  filtered: boolean;
  onClear: () => void;
  /** Anything the page adds after the count, already a sentence or fragment. */
  children?: ReactNode;
}) {
  const plural = (count: number) => noun[count === 1 ? 0 : 1];
  const counted = total === null || !filtered
    ? `${shown} ${plural(shown)}`
    : `${shown} of ${total} ${plural(total)}`;

  return (
    <HStack gap={2} flexWrap="wrap" minH={8}>
      <Text fontSize="sm" color="fg.muted">
        {counted}
        {children}
      </Text>
      {filtered ? (
        <Button size="xs" variant="ghost" colorPalette="blue" onClick={onClear}>
          <LuX />
          Clear filters
        </Button>
      ) : null}
    </HStack>
  );
}
